/**
 * Follow-Up Context
 *
 * Manages state for the follow-up flow.
 * Supports email-based user identification for follow-up vs new users.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  checkEmailExists,
  getSubmissionDataByEmail,
  updateModules as updateModulesApi,
  generateFollowUpPDF,
  createNewSubmission as createNewSubmissionApi,
} from '../services/followupService';
import { environment } from '../env/environment';
import { secureSessionStorage } from '../utils/secure-session';
import logger from '../utils/logger';
import activityLogger from '../utils/activityLogger';

const FollowUpContext = createContext(null);
const SESSION_STORAGE_KEY = 'followup_form_state';

// Helper function to normalize field names to lowercase without special characters
// This matches the database format: all lowercase, no special characters
const normalizeFieldName = (fieldName) => {
  if (!fieldName || typeof fieldName !== 'string') return '';
  // Remove ALL non-alphanumeric characters and convert to lowercase
  return fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
};

// Helper function to normalize all keys in an object
const normalizeFormData = (data) => {
  if (!data || typeof data !== 'object') return {};

  const normalized = {};

  Object.keys(data).forEach(key => {
    const value = data[key];
    const normalizedKey = normalizeFieldName(key);

    // Store both original and normalized keys
    normalized[key] = value;
    if (normalizedKey !== key) {
      normalized[normalizedKey] = value;
    }
  });

  return normalized;
};

export const useFollowUp = () => {
  const context = useContext(FollowUpContext);
  if (!context) {
    throw new Error('useFollowUp must be used within a FollowUpProvider');
  }
  return context;
};

export const FollowUpProvider = ({ children }) => {
  // Ref to track if session has been restored
  const sessionRestored = useRef(false);

  // Loading and error state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // User identification (restored from sessionStorage via useEffect below)
  const [userEmail, setUserEmail] = useState(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  // Submission data (restored from sessionStorage via useEffect below)
  const [submissionId, setSubmissionId] = useState(null);
  const [submissionInfo, setSubmissionInfo] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [phoneHint, setPhoneHint] = useState('');
  const [painDiagramData, setPainDiagramData] = useState(null);
  const [tenantId, setTenantId] = useState(null);

  // Modules (restored from sessionStorage via useEffect below)
  const [availableModules, setAvailableModules] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [fieldsMap, setFieldsMap] = useState({});

  // Form state (restored from sessionStorage via useEffect below)
  const [updatedFields, setUpdatedFields] = useState({});
  const [currentStep, setCurrentStep] = useState(0);

  // OTP verification state (restored from sessionStorage via useEffect below)
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSessionId, setOtpSessionId] = useState(null);

  // Restore state from sessionStorage on mount (async because getItem is async)
  useEffect(() => {
    (async () => {
      if (typeof window === 'undefined') return;
      try {
        const parsed = await secureSessionStorage.getItem(SESSION_STORAGE_KEY);
        if (parsed) {
          if (parsed.userEmail !== undefined) setUserEmail(parsed.userEmail);
          if (parsed.isExistingUser !== undefined) setIsExistingUser(parsed.isExistingUser);
          if (parsed.isNewUser !== undefined) setIsNewUser(parsed.isNewUser);
          if (parsed.submissionId !== undefined) setSubmissionId(parsed.submissionId);
          if (parsed.submissionInfo !== undefined) setSubmissionInfo(parsed.submissionInfo);
          if (parsed.previousData !== undefined) setPreviousData(parsed.previousData);
          if (parsed.painDiagramData !== undefined) setPainDiagramData(parsed.painDiagramData);
          if (parsed.tenantId !== undefined) setTenantId(parsed.tenantId);
          if (parsed.availableModules !== undefined) setAvailableModules(parsed.availableModules);
          if (parsed.allModules !== undefined) setAllModules(parsed.allModules);
          if (parsed.selectedModules !== undefined) setSelectedModules(parsed.selectedModules);
          if (parsed.fieldsMap !== undefined) setFieldsMap(parsed.fieldsMap);
          if (parsed.updatedFields !== undefined) setUpdatedFields(parsed.updatedFields);
          if (parsed.currentStep !== undefined) setCurrentStep(parsed.currentStep);
          if (parsed.otpVerified !== undefined) setOtpVerified(parsed.otpVerified);
          if (parsed.otpSessionId !== undefined) setOtpSessionId(parsed.otpSessionId);
        }
      } catch (e) {
        logger.error('Error reading from sessionStorage:', e);
      }
      // Mark session as restored so the save effect starts persisting
      sessionRestored.current = true;
    })();
  }, []);

  // Save state to sessionStorage whenever relevant state changes
  useEffect(() => {
    // Only save after session has been restored from storage
    if (!sessionRestored.current) {
      return;
    }

    const stateToSave = {
      userEmail,
      isExistingUser,
      isNewUser,
      submissionId,
      submissionInfo,
      previousData,
      painDiagramData,
      tenantId,
      availableModules,
      allModules,
      selectedModules,
      fieldsMap,
      updatedFields,
      currentStep,
      otpVerified,
      otpSessionId,
    };

    secureSessionStorage.setItem(SESSION_STORAGE_KEY, stateToSave).catch((e) => {
      logger.error('Error saving to sessionStorage:', e);
    });
  }, [
    userEmail,
    isExistingUser,
    isNewUser,
    submissionId,
    submissionInfo,
    previousData,
    painDiagramData,
    tenantId,
    availableModules,
    allModules,
    selectedModules,
    fieldsMap,
    updatedFields,
    currentStep,
    otpVerified,
    otpSessionId,
  ]);

  // Keep the activity logger pointed at the current patient. This also re-seeds
  // it after a reload, when its in-memory context is gone but ours is restored.
  // submissionId is deliberately left out: ours is the public submission_id
  // string, and the log table's submission_id is the integer row id.
  useEffect(() => {
    const info = submissionInfo?.patientInfo;
    activityLogger.setContext({
      email: userEmail,
      tenantId,
      patientName: [info?.firstName, info?.lastName].filter(Boolean).join(' '),
    });
  }, [userEmail, tenantId, submissionInfo]);

  useEffect(() => {
    const handleGlobalError = (event) => activityLogger.browserError(event);
    const handleRejection = (event) => activityLogger.browserError(event.reason || { message: 'Unhandled promise rejection' });
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  /**
   * Check if email exists in database
   * Returns whether user is existing or new
   */
  const checkEmail = useCallback(async (email) => {
    setIsLoading(true);
    setError(null);
    setUserEmail(email);

    try {
      const result = await checkEmailExists(email);

      // Check lockout FIRST — locked users see lockout, not "new patient" popup
      if (result.status === 'locked' || result.status === 'permanent_lock') {
        return {
          isExisting: false,
          isLocked: true,
          status: result.status,
          message: result.message,
          locked_until: result.locked_until,
          remaining_ms: result.remaining_ms,
          lockout_stage: result.lockout_stage,
        };
      }

      if (result.success && result.exists) {
        // Existing user
        setIsExistingUser(true);
        setIsNewUser(false);
        setSubmissionId(result.submission?.submissionId);

        // Only a masked phone for the code screen. Their intake is loaded after they verify the code.
        setPhoneHint(result.submission?.phoneHint || '');

        return { isExisting: true, submission: result.submission };
      } else {
        // New user - will use full intake flow
        setIsExistingUser(false);
        setIsNewUser(true);
        return { isExisting: false };
      }
    } catch (err) {
      setError(err.message || 'Failed to check email');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load the patient's previous intake for prefilling, after they verified their code.
   */
  const loadSubmissionData = useCallback(async (email) => {
    setIsLoading(true);
    setError(null);

    try {
      // Needs the session from verifying the code (sent by getSubmissionDataByEmail).
      const submissionData = await getSubmissionDataByEmail(email);
      if (submissionData.success) {
        // Normalize field names for consistent access
        const normalizedData = normalizeFormData(submissionData.formData || {});

        // Process _uploadedImages to make Google Drive URLs accessible
        // Convert to format that components can display
        const uploadedImages = submissionData.formData?._uploadedImages || {};
        const processedImageFields = {};

        Object.entries(uploadedImages).forEach(([fieldName, images]) => {
          if (Array.isArray(images) && images.length > 0) {
            // Store both the original field and a display-ready version
            processedImageFields[fieldName] = images.map(img => ({
              name: img.name || 'Uploaded Image',
              url: img.url,
              preview: img.url, // Use Google Drive URL as preview
              mimeType: img.mimeType,
              uploadedAt: img.uploadedAt,
              isGoogleDrive: true
            }));

            // Also store for document upload fields (dl_front, dl_back, etc.)
            if (fieldName.includes('dl_') || fieldName.includes('primary_') ||
                fieldName.includes('secondary_') || fieldName.includes('tertiary_')) {
              processedImageFields[`${fieldName}_googleDrive`] = images[0]?.url;
            }
          }
        });

        // Merge processed image fields with normalized data
        const dataWithImages = {
          ...normalizedData,
          _processedImages: processedImageFields,
          _uploadedImages: uploadedImages
        };

        logger.log('📷 [FollowUpContext] Processed image fields:', processedImageFields);

        setPreviousData(dataWithImages);
        setAvailableModules(submissionData.modules || []);

        // Store tenant_id for subsequent API calls
        const currentTenantId = submissionData.tenantId;
        setTenantId(currentTenantId);
        logger.log('🏢 [FollowUpContext] Stored tenant_id:', currentTenantId);

        // Also fetch ALL modules and their fields for complete PDF generation
        // Use different API URL based on tenant presence
        try {
          const modulesUrl = currentTenantId
            ? `${environment.baseUrl}/api/followup-modules/getAllModules?tenantId=${currentTenantId}`
            : `${environment.baseUrl}/api/followup-modules/getAllModules`;
          logger.log('📊 [FollowUpContext] Fetching modules from:', environment.baseUrl);
          const allModulesResponse = await fetch(modulesUrl);
          const allModulesData = await allModulesResponse.json();
          if (allModulesData.modules) {
            const sorted = allModulesData.modules.sort((a, b) => a.count - b.count);
            setAllModules(sorted);
            logger.log('📊 [FollowUpContext] Fetched all modules for PDF:', sorted.length);

            // Fetch fields for ALL modules using same API URL
            const fieldsMapData = {};
            for (const mod of sorted) {
              try {
                const fieldsResponse = await fetch(
                  `${environment.baseUrl}/api/followup-modules/getFields/${mod.module_name}${currentTenantId ? `?tenantId=${currentTenantId}` : ''}`
                );
                const fieldsData = await fieldsResponse.json();
                fieldsMapData[mod.module_name] = fieldsData.fields || [];
              } catch (fieldErr) {
                logger.error(`Error fetching fields for ${mod.module_name}:`, fieldErr);
                fieldsMapData[mod.module_name] = [];
              }
            }
            setFieldsMap(fieldsMapData);
            logger.log('📊 [FollowUpContext] Fetched fields for all modules:', Object.keys(fieldsMapData).length);
          }
        } catch (moduleErr) {
          logger.error('Failed to fetch all modules:', moduleErr);
          // Fallback to available modules if fetch fails
          setAllModules(submissionData.modules || []);
        }

        // Extract pain diagram data if available (try multiple key formats)
        const painData = submissionData.formData?.painDiagramData
          || submissionData.formData?.paindiagramdata
          || normalizedData?.painDiagramData
          || normalizedData?.paindiagramdata;
        if (painData) {
          logger.log('📊 [FollowUpContext] Pain diagram data found:', painData);
          setPainDiagramData(painData);
        }

        setSubmissionInfo({
          submissionId: submissionData.submissionId,
          patientInfo: submissionData.patientInfo,
          originalSubmissionDate: submissionData.originalSubmissionDate,
          followUpCount: submissionData.followUpCount || 0,
        });
      }
      return submissionData;
    } catch (err) {
      setError(err.message || 'Failed to load your previous answers');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch all modules (for new users - full intake flow)
   */
  const fetchAllModules = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${environment.baseUrl}/api/followup-modules/getAllModules`
      );
      const data = await response.json();

      if (data.modules) {
        const sorted = data.modules.sort((a, b) => a.count - b.count);
        setAllModules(sorted);
        return sorted;
      }
      return [];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch fields for a specific module
   */
  const fetchModuleFields = useCallback(async (moduleName) => {
    try {
      const response = await fetch(
        `${environment.baseUrl}/api/followup-modules/getFields/${moduleName}`
      );
      const data = await response.json();
      return data.fields || [];
    } catch (err) {
      logger.error(`Error fetching fields for ${moduleName}:`, err);
      return [];
    }
  }, []);

  /**
   * Toggle module selection
   */
  const toggleModule = useCallback((moduleId) => {
    setSelectedModules((prev) => {
      if (prev.includes(moduleId)) {
        return prev.filter((id) => id !== moduleId);
      }
      return [...prev, moduleId];
    });
  }, []);

  /**
   * Update a single field
   */
  const updateField = useCallback((fieldName, value) => {
    setUpdatedFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  }, []);

  /**
   * Update multiple fields
   */
  const updateFields = useCallback((fields) => {
    setUpdatedFields((prev) => ({
      ...prev,
      ...fields,
    }));
  }, []);

  /**
   * Get merged form data (previous + updated)
   */
  const getMergedFormData = useCallback(() => {
    return {
      ...(previousData || {}),
      ...updatedFields,
    };
  }, [previousData, updatedFields]);

  /**
   * Flatten authorized contacts array to individual fields for GHL/backend
   * [{firstName: "John", lastName: "Doe"}] → {authorizedcontact1firstname: "John", authorizedcontact1lastname: "Doe"}
   */
  const flattenAuthorizedContacts = useCallback((values) => {
    const processed = { ...values };
    const keysToRemove = [];

    Object.keys(processed).forEach(key => {
      const lowerKey = key.toLowerCase();
      const isContactArray = (lowerKey === 'authorizedcontacts' || lowerKey === 'authorized_contacts' ||
        lowerKey === 'authorizedcontact' || (lowerKey.includes('authorizedcontact') && !lowerKey.match(/authorizedcontact\d/)));

      if (isContactArray && Array.isArray(processed[key]) && processed[key].length > 0 && typeof processed[key][0] === 'object') {
        processed[key].forEach((contact, idx) => {
          if (!contact || typeof contact !== 'object') return;
          if (!contact.firstName && !contact.lastName && !contact.email && !contact.phone) return;

          const prefix = `authorizedcontact${idx + 1}`;
          if (contact.firstName) processed[`${prefix}firstname`] = contact.firstName;
          if (contact.lastName) processed[`${prefix}lastname`] = contact.lastName;
          if (contact.email) processed[`${prefix}email`] = contact.email;
          if (contact.phone) processed[`${prefix}phone`] = contact.phone;
          if (contact.relationship) {
            processed[`${prefix}relationship`] = contact.relationship === 'Other' && contact.otherRelationship
              ? `Other - ${contact.otherRelationship}` : contact.relationship;
          }
          processed[`${prefix}isemergency`] = contact.isEmergencyContact ? 'Yes' : 'No';
          processed[`${prefix}ismedicalauth`] = contact.isAuthorizedMedical ? 'Yes' : 'No';
        });

        const validCount = processed[key].filter(c => c?.firstName || c?.lastName || c?.email || c?.phone).length;
        processed['authorizedcontactscount'] = validCount;
        keysToRemove.push(key);
      }
    });

    keysToRemove.forEach(k => delete processed[k]);
    return processed;
  }, []);

  /**
   * Process accordion fields - convert raw checkboxes to readable format
   * e.g. fieldname_section_0_checkbox_2: true → "Cardiovascular": { question: "Cardiovascular", selectedValues: ["High BP"] }
   */
  const processAccordionFields = useCallback((values) => {
    const processed = { ...values };
    const keysToRemove = [];

    Object.entries(fieldsMap).forEach(([moduleName, fields]) => {
      fields.forEach(field => {
        if (field.type === 'accordion' && field.accordionSections) {
          const baseFieldName = (field.field_name || field.name || field.labelName || '')
            .toLowerCase().replace(/[^a-z0-9]/g, '');

          field.accordionSections.forEach((section, sectionIndex) => {
            const sectionFieldName = `${baseFieldName}_section_${sectionIndex}`;
            const selectedValues = [];

            section.checkboxes?.forEach((checkbox, checkboxIndex) => {
              const checkboxKey = `${sectionFieldName}_checkbox_${checkboxIndex}`;

              if (values[checkboxKey] === true || values[checkboxKey] === 'true') {
                selectedValues.push(checkbox.label);

                // Collect nested inputs
                if (checkbox.nestedInputs?.length > 0) {
                  checkbox.nestedInputs.forEach((_, inputIndex) => {
                    keysToRemove.push(`${checkboxKey}_input_${inputIndex}`);
                  });
                }
              }
              keysToRemove.push(checkboxKey);
            });

            if (selectedValues.length > 0) {
              const sectionSlug = section.title.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/^_|_$/g, '');
              processed[`accordion_${sectionSlug}`] = {
                question: section.title,
                selectedValues,
              };
            }
          });
        }
      });
    });

    // Remove raw checkbox keys
    keysToRemove.forEach(key => delete processed[key]);
    return processed;
  }, [fieldsMap]);

  /**
   * Submit updates for existing user or create new submission
   */
  const submitUpdates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const currentTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

      // Process fields to readable format before sending
      let processedFields = processAccordionFields(updatedFields);
      processedFields = flattenAuthorizedContacts(processedFields);

      // New user: create a new submission first
      if (isNewUser && !submissionId) {
        logger.log('🆕 [FollowUpContext] New user - creating submission...');
        const mergedData = { ...(previousData || {}), ...processedFields };
        const createResult = await createNewSubmissionApi(userEmail, mergedData, currentTenantId);

        if (createResult.success && createResult.submissionId) {
          setSubmissionId(createResult.submissionId);
          logger.log('✅ [FollowUpContext] New submission created:', createResult.submissionId);

          // Clear sessionStorage after successful submission
          try { secureSessionStorage.removeItem(SESSION_STORAGE_KEY); } catch (e) {}
          return createResult;
        } else {
          throw new Error(createResult.message || 'Failed to create submission');
        }
      }

      // Existing user: update existing submission
      if (!submissionId) {
        throw new Error('No submission ID');
      }

      logger.log('🔗 [FollowUpContext] Updating existing submission:', submissionId);
      const result = await updateModulesApi({
        submissionId,
        selectedModules,
        updatedFields: processedFields,
        tenantId: currentTenantId,
      });

      // Clear sessionStorage after successful submission
      try { secureSessionStorage.removeItem(SESSION_STORAGE_KEY); } catch (e) {}

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [submissionId, selectedModules, updatedFields, tenantId, isNewUser, userEmail, previousData, processAccordionFields, flattenAuthorizedContacts]);

  /**
   * Generate follow-up PDF
   */
  const generatePDF = useCallback(async (pdfBlob, painDiagramBlob, overrideSubmissionId = null) => {
    const sid = overrideSubmissionId || submissionId;
    if (!sid) {
      logger.warn('[FollowUpContext] No submission ID for PDF generation - skipping upload');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get tenant ID for proper isolation
      const currentTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');
      logger.log('🔗 [FollowUpContext] Generating PDF with submission:', sid, 'tenant:', currentTenantId);

      const result = await generateFollowUpPDF({
        submissionId: sid,
        pdfBlob,
        painDiagramBlob,
        tenantId: currentTenantId,
      });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [submissionId, tenantId]);

  /**
   * Navigation helpers
   */
  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  /**
   * Reset form state
   */
  const reset = useCallback(() => {
    setSelectedModules([]);
    setUpdatedFields({});
    setCurrentStep(0);
    setError(null);
  }, []);

  /**
   * Clear all state (full reset) and sessionStorage
   */
  const clearAll = useCallback(() => {
    setUserEmail(null);
    setIsExistingUser(false);
    setIsNewUser(false);
    setSubmissionId(null);
    setSubmissionInfo(null);
    setPreviousData(null);
    setPhoneHint('');
    setPainDiagramData(null);
    setAvailableModules([]);
    setAllModules([]);
    setSelectedModules([]);
    setFieldsMap({});
    setUpdatedFields({});
    setCurrentStep(0);
    setError(null);
    setIsLoading(false);
    setOtpVerified(false);
    setOtpSessionId(null);
    activityLogger.clearContext();

    // Clear sessionStorage
    try {
      secureSessionStorage.removeItem(SESSION_STORAGE_KEY);
      secureSessionStorage.removeItem('otp_session_id');
      secureSessionStorage.removeItem('otp_verified_email');
    } catch (e) {
      logger.error('Error clearing sessionStorage:', e);
    }
  }, []);

  const value = {
    // State
    isLoading,
    error,
    userEmail,
    isExistingUser,
    isNewUser,
    submissionId,
    submissionInfo,
    previousData,
    painDiagramData,
    availableModules,
    allModules,
    selectedModules,
    fieldsMap,
    updatedFields,
    currentStep,
    otpVerified,
    otpSessionId,

    // Actions
    checkEmail,
    loadSubmissionData,
    phoneHint,
    fetchAllModules,
    fetchModuleFields,
    toggleModule,
    updateField,
    updateFields,
    getMergedFormData,
    submitUpdates,
    generatePDF,

    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,

    // Reset
    reset,
    clearAll,

    // Setters
    setSelectedModules,
    setUpdatedFields,
    setError,
    setCurrentStep,
    setIsExistingUser,
    setIsNewUser,
    setFieldsMap,
    setAllModules,
    setPreviousData,
    setPainDiagramData,
    setOtpVerified,
    setOtpSessionId,
  };

  return (
    <FollowUpContext.Provider value={value}>
      {children}
    </FollowUpContext.Provider>
  );
};

export default FollowUpContext;
